import { useQuery } from '@tanstack/react-query'
import { createContext, useContext, useEffect, type ReactNode } from 'react'

import { useApi } from './ApiContext'
import { DEFAULT_REMOTE_CONFIG, featureEnabled, readCachedRemoteConfig, writeCachedRemoteConfig } from './remoteConfig'
import type { RemoteConfig } from './types'

export const CONFIG_QUERY_KEY = ['config'] as const

const RemoteConfigContext = createContext<RemoteConfig | null>(null)

/**
 * `GET /config` (docs/plan/api.md "App config"), fetched on load with TanStack Query: feature switches,
 * maintenance, the version floor and `consentVersion`. Starts from localStorage's last-known answer so there's
 * something to show offline, and keeps that cache fresh as new answers come in. `cache-control: public,
 * max-age=300` on the server, matched here with the same `staleTime` so a mount mid-session doesn't refetch early.
 */
export function RemoteConfigProvider(props: { children: ReactNode }) {
  const api = useApi()
  const query = useQuery({
    queryKey: CONFIG_QUERY_KEY,
    queryFn: () => api.getConfig(),
    initialData: () => readCachedRemoteConfig() ?? undefined,
    staleTime: 5 * 60_000,
  })

  useEffect(() => {
    if (query.data) writeCachedRemoteConfig(query.data)
  }, [query.data])

  return <RemoteConfigContext.Provider value={query.data ?? DEFAULT_REMOTE_CONFIG}>{props.children}</RemoteConfigContext.Provider>
}

export function useRemoteConfig(): RemoteConfig {
  const config = useContext(RemoteConfigContext)
  if (!config) throw new Error('useRemoteConfig must be used inside RemoteConfigProvider')
  return config
}

/** Whether server feature [key] (docs/plan/api.md's `features` map) is on; missing reads as on. */
export function useFeatureEnabled(key: string): boolean {
  return featureEnabled(useRemoteConfig(), key)
}
